import { FormikErrors, useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess, TaxType } from "Components/constants/Common";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  listOfProductForDropdown,
  listOfVendorForDropdown,
  manufacturerListApi,
} from "Api/common";
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  viewIndent,
  viewPurchaseOrder,
} from "Api/PurchaseOrder";
import {
  cancelLabel,
  DeleteLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import moment from "moment";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import {
  AddProductLabel,
  AddPurchaseOrderLabel,
  AmountLabel,
  AvailableQuantityLabel,
  DeliveryDateKey,
  EditPurchaseOrderLabel,
  ExpectedDeliveryDateLabel,
  IndentIdKey,
  IndentIdLabel,
  OrderDateKey,
  OrderDateLabel,
  ProductCodeLabel,
  ProductsKey,
  QuantityLabel,
  RateLabel,
  TaxLabel,
  TotalCostLabel,
  VendorIdLabel,
  VendorNameFormikKey,
  VendorNameLabel,
} from "Components/constants/PurchaseOrder";
import { makeLabel } from "Components/constants/ManuFacturer";

interface Product {
  manufacturer_id: number | null;
  productCode: string;
  availableQuantity: any;
  rate: number;
  quantity: any;
  tax: string;
  amount: number;
}

interface ProductsInitialValues {
  indent_id: number;
  vendor_name: string;
  vendor_id: number;
  order_date: string;
  delivery_date: string;
  total_cost: number;
  products: Product[];
}

const validationSchema = yup.object().shape({
  vendor_name: yup
    .string()
    .required(validationMessages.required(VendorNameLabel)),
  order_date: yup
    .string()
    .required(validationMessages.required(OrderDateLabel)),
  delivery_date: yup
    .string()
    .required(validationMessages.required(ExpectedDeliveryDateLabel))
    .when("order_date", (orderDate: any, schema) => {
      return schema.test({
        name: "delivery_date",
        exclusive: false,
        message: "Delivery date should be more than or equal to order date",
        test: function (deliveryDate) {
          if (!deliveryDate || !orderDate) return true;
          return new Date(deliveryDate) >= new Date(orderDate);
        },
      });
    }),
  products: yup.array().of(
    yup.object().shape({
      productCode: yup
        .string()
        .required(validationMessages.required(ProductCodeLabel)),
      rate: yup
        .number()
        .min(1, validationMessages.positiveNumber(RateLabel))
        .required(validationMessages.required(RateLabel)),
      quantity: yup
        .number()
        .min(1, validationMessages.positiveNumber(QuantityLabel))
        .required(validationMessages.required(QuantityLabel)),
      tax: yup.string().required(validationMessages.required(TaxLabel)),
      manufacturer_id: yup
        .string()
        .required(validationMessages.required(makeLabel)),
    })
  ),
});

const AddPurchaseOrder = () => {
  const navigate = useNavigate();
  const { id, editId } = useParams();
  document.title = editId ? EditPurchaseOrderLabel : AddPurchaseOrderLabel;
  const [loader, setLoader] = useState(false);
  const [viewStaffData, setViewStaffData] = useState<any>([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [productId, setProductId] = useState<number[]>([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);

  const fetchProduct = () => {
    setLoader(true);
    let data;
    listOfProductForDropdown()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setProducts(
            data?.map((item: any) => ({
              value: item?.id,
              label: `${item?.product_code} (${item.batch_no})`,
              quantity: item?.quantity,
              rate: item?.rate,
              manufacturer_id: item?.manufacturer?.id,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchManufacturer = () => {
    setLoader(true);
    let data;
    manufacturerListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setManufacturers(
            data?.map((item: any) => ({
              value: item?.id,
              label: item.name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchVendor = () => {
    setLoader(true);
    let data;
    listOfVendorForDropdown()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setVendors(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.name,
              draft_id: item?.user_draft_id,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const initialValues: ProductsInitialValues = {
    indent_id: 0,
    vendor_name: "",
    vendor_id: 0,
    order_date: "",
    delivery_date: "",
    total_cost: 0,
    products: [
      {
        manufacturer_id: null,
        productCode: "",
        availableQuantity: 0,
        rate: 0,
        tax: "",
        quantity: 0,
        amount: 0,
      },
    ],
  };
  const formik = useFormik<ProductsInitialValues>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        indent_id: values.indent_id,
        order_date: values.order_date,
        auth_id: +values.vendor_name,
        delivery_date: values.delivery_date,
        total_cost: values.total_cost,
        purchaseOrderItem: values.products.map((item) => {
          return {
            manufacturer_id: item.manufacturer_id,
            product_id: item.productCode,
            rate: +item.rate,
            amount: +item.amount,
            tax: item.tax,
            product_quantity: +item.quantity,
            received_quantity: +item.quantity,
            pending_quantity: +item.quantity,
          };
        }),
      };
      const { indent_id, ...editPayload } = payload;
      setBtnLoader(true);
      if (editId) {
        updatePurchaseOrder(Number(editId), editPayload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/purchase-order");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createPurchaseOrder(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/purchase-order");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    if (viewStaffData) {
      const transformedProducts = editId
        ? viewStaffData?.purchaseOrderItem?.map((item: any) => {
            return {
              productCode: item.product.id,
              availableQuantity: item.product.quantity,
              manufacturer_id: item?.manufacturer?.id,
              rate: item?.rate,
              tax: item?.tax,
              quantity: item?.product_quantity,
            };
          })
        : viewStaffData?.indentItem?.map((item: any) => {
            return {
              productCode: item.product_id,
              availableQuantity: item.product.quantity,
              manufacturer_id: null,
              tax: "",
              rate: item.product?.rate,
              quantity: item.quantity,
            };
          });
      formik.setFieldValue(ProductsKey, transformedProducts);
      formik.setFieldValue(IndentIdKey, viewStaffData?.id);
      editId &&
        formik.setFieldValue(VendorNameFormikKey, viewStaffData?.auth_id);
      editId &&
        formik.setFieldValue(
          OrderDateKey,
          moment(viewStaffData?.order_date).format().split("T")?.[0]
        );
      editId &&
        formik.setFieldValue(
          DeliveryDateKey,
          moment(viewStaffData?.order_date).format().split("T")?.[0]
        );
    }
  }, [viewStaffData]);

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  const fetchViewRole = () => {
    if (id) {
      setLoader(true);
      viewIndent(Number(id))
        .then((response) => {
          if (checkStatusCodeSuccess(response.statusCode)) {
            const roleData = response.data;
            setViewStaffData(roleData);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
    if (editId) {
      setLoader(true);
      viewPurchaseOrder(Number(editId))
        .then((response) => {
          if (checkStatusCodeSuccess(response.statusCode)) {
            const roleData = response.data;
            setViewStaffData(roleData);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    if (id || editId) {
      fetchViewRole();
    }
    if (!id) {
      const newProduct = [
        {
          productCode: "",
          manufacturer_id: null,
          availableQuantity: 0,
          rate: 0,
          quantity: 0,
        },
      ];
      formik.setFieldValue(ProductsKey, newProduct);
    }
    fetchManufacturer();
    fetchProduct();
    fetchVendor();
  }, []);

  useEffect(() => {
    Array.isArray(formik.values.products) &&
      formik.values.products.forEach((product: any, index: number) => {
        setProductId((prevProductIds) => {
          if (index !== -1) {
            const updatedProductIds = [...prevProductIds];
            updatedProductIds[index] = Number(product?.productCode);
            return updatedProductIds;
          } else {
            return [...prevProductIds, Number(product?.productCode)];
          }
        });     
        const result: any = products.find((item: any) => item.value === product.productCode);
        const amount: number = Number(product.quantity) * Number(product.rate);
        const tax: number = Number(product.tax?.split("_")[0]);
        const finalAmount: number = amount + (amount * tax) / 100;

        if (result) {
          formik.setFieldValue(`products[${index}].availableQuantity`, result.quantity);
  
          if (!editId && productId[index] !== product.productCode) {
            formik.setFieldValue(`products[${index}].rate`, result.rate);
            formik.setFieldTouched(`products[${index}].rate`, false);
          }
          if (productId[index] !== product.productCode) {
            formik.setFieldValue(`products[${index}].manufacturer_id`, result?.manufacturer_id);
            formik.setFieldTouched(`products[${index}].manufacturer_id`, true);
          }
        }
  
        if (finalAmount) {
          formik.setFieldValue(`products[${index}].amount`, finalAmount);
        }
      });
  
    let total_cost = formik.values.products?.reduce((acc: any, item: any) => acc + item.amount, 0);
    formik.setFieldValue("total_cost", Number(total_cost));
  }, [formik.values.products]);
  

  useEffect(() => {
    let result: any = vendors.find((item: any) => {
      return item.value === formik.values.vendor_name;
    });
    formik.setFieldValue("vendor_id", result?.draft_id);
  }, [formik.values.vendor_name]);

  const addProductRow = () => {
    const newProduct = {
      productCode: "",
      manufacturer_id: "",
      availableQuantity: 0,
      rate: 0,
      quantity: 0,
    };

    const currentProducts = formik.values.products || [];

    formik.setFieldValue(ProductsKey, [...currentProducts, newProduct]);
  };

  const removeProductRow = (index: number) => {
    const products = [...formik.values.products];
    products.splice(index, 1);
    formik.setFieldValue(ProductsKey, products);
  };

  const getFilteredProducts = (index: number) => {
    const selectedProductCodes = formik.values.products
      .map((product, idx) => (idx !== index ? product.productCode : null))
      .filter(Boolean);

    return products.filter(
      (product: any) => !selectedProductCodes.includes(product.value)
    );
  };

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {editId ? EditPurchaseOrderLabel : AddPurchaseOrderLabel}
          </span>
          <button
            className="btn btn-danger"
            onClick={() =>
              navigate(editId ? "/purchase-order" : "/indentation")
            }
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body ">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="d-flex justify-content-end">
                <BaseButton
                  type="button"
                  label={AddProductLabel}
                  color="info"
                  onClick={() => addProductRow()}
                />
              </div>
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="indent_id"
                    label={IndentIdLabel}
                    value={formik.values.indent_id}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.indent_id}
                    touched={formik.touched.indent_id}
                    disabled
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseSelect
                    name="vendor_name"
                    label={VendorNameLabel}
                    options={vendors}
                    placeholder={SelectPlaceHolder(VendorNameLabel)}
                    value={formik.values.vendor_name}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.vendor_name}
                    touched={formik.touched.vendor_name}
                    isDisabled={editId}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="vendor_id"
                    label={VendorIdLabel}
                    placeholder={InputPlaceHolder(VendorIdLabel)}
                    value={formik.values.vendor_id}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.vendor_id}
                    touched={formik.touched.vendor_id}
                    disabled
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="order_date"
                    label={OrderDateLabel}
                    type="date"
                    value={formik.values.order_date}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.order_date}
                    touched={formik.touched.order_date}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-2">
                  <BaseInput
                    name="delivery_date"
                    label={ExpectedDeliveryDateLabel}
                    type="date"
                    value={formik.values.delivery_date}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.delivery_date}
                    touched={formik.touched.delivery_date}
                    required
                  />
                </div>
              </div>
              <div className="border-top my-3"></div>

              <div className="">
                {Array.isArray(formik.values.products) &&
                  formik.values.products.map(
                    (product: Product, index: number) => (
                      <div
                        className={
                          index % 2 === 0 ? "bg-wheat p-2" : "bg-white p-2"
                        }
                      >
                        <div className="row pb-3 mt-3" key={index}>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseSelect
                              name={`products[${index}].productCode`}
                              label={ProductCodeLabel}
                              options={getFilteredProducts(index) || []}
                              value={formik.values.products[index].productCode}
                              placeholder={SelectPlaceHolder(ProductCodeLabel)}
                              handleChange={(field: any, value: any) => {
                                formik.setFieldValue(field, value);
                              }}
                              handleBlur={formik.handleBlur}
                              error={
                                formik.touched.products?.[index]?.productCode &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.productCode
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).productCode
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]?.productCode ||
                                false
                              }
                              required
                            />
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseSelect
                              name={`products[${index}].manufacturer_id`}
                              label={makeLabel}
                              options={manufacturers || []}
                              value={
                                formik.values.products[index].manufacturer_id
                              }
                              placeholder={SelectPlaceHolder(makeLabel)}
                              handleChange={(field: any, value: any) => {
                                formik.setFieldValue(field, value);
                              }}
                              handleBlur={formik.handleBlur}
                              error={
                                formik.touched.products?.[index]
                                  ?.manufacturer_id &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.manufacturer_id
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).manufacturer_id
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]
                                  ?.manufacturer_id || false
                              }
                              required
                            />
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseInput
                              name={`products[${index}].rate`}
                              label={RateLabel}
                              type="number"
                              value={formik.values.products[index].rate}
                              onChange={formik.handleChange}
                              handleBlur={formik.handleBlur}
                              error={
                                formik.touched.products?.[index]?.rate &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.rate
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).rate
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]?.rate || false
                              }
                              required
                            />
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseInput
                              name={`products[${index}].availableQuantity`}
                              label={AvailableQuantityLabel}
                              value={
                                formik.values.products[index].availableQuantity
                              }
                              onChange={formik.handleChange}
                              handleBlur={formik.handleBlur}
                              disabled
                              error={
                                formik.touched.products?.[index]
                                  ?.availableQuantity &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.availableQuantity
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).availableQuantity
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]
                                  ?.availableQuantity || false
                              }
                            />
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseInput
                              name={`products[${index}].quantity`}
                              type="number"
                              label={QuantityLabel}
                              value={formik.values.products[index].quantity}
                              onChange={formik.handleChange}
                              handleBlur={formik.handleBlur}
                              error={
                                formik.touched.products?.[index]?.quantity &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.quantity
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).quantity
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]?.quantity ||
                                false
                              }
                              required
                            />
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseSelect
                              name={`products[${index}].tax`}
                              label={TaxLabel}
                              options={TaxType || []}
                              value={formik.values.products[index].tax}
                              placeholder={SelectPlaceHolder(TaxLabel)}
                              handleChange={(field: any, value: any) => {
                                formik.setFieldValue(field, value);
                              }}
                              handleBlur={formik.handleBlur}
                              error={
                                formik.touched.products?.[index]?.tax &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.tax
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).tax
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]?.tax || false
                              }
                              required
                            />
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <BaseInput
                              name={`products[${index}].amount`}
                              label={AmountLabel}
                              prepend={
                                <i className="mdi mdi-currency-rupee"></i>
                              }
                              value={formik.values.products[index].amount?.toFixed(2)}
                              onChange={formik.handleChange}
                              handleBlur={formik.handleBlur}
                              error={
                                formik.touched.products?.[index]?.amount &&
                                (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                )?.amount
                                  ? (
                                      formik.errors.products?.[
                                        index
                                      ] as FormikErrors<Product>
                                    ).amount
                                  : ""
                              }
                              touched={
                                formik.touched.products?.[index]?.amount ||
                                false
                              }
                              disabled
                              required
                            />
                          </div>
                          {index > 0 && (
                            <div className="col-1 p-2 d-flex justify-content-start align-items-end">
                              <i className="mdi mdi-delete-outline text-danger fs-4 p-0 cursor-pointer" onClick={() => removeProductRow(index)}></i>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )}
              </div>
              <div className="d-flex justify-content-end row mt-4 border-top">
                <div className="col-md-2 col-sm-6 col-12 mt-2">
                  <BaseInput
                    name={`total_cost`}
                    label={TotalCostLabel}
                    type="number"
                    prepend={<i className="mdi mdi-currency-rupee"></i>}
                    value={formik.values.total_cost?.toFixed(2)}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.total_cost}
                    touched={formik.touched.total_cost}
                    disabled
                    required
                  />
                </div>
              </div>
              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={editId ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/indentation")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPurchaseOrder;
